import { useEffect, useMemo, useState } from "react";
import { LeagueService } from "@/services/leagueService";
import { matchCountryBySlug } from "@/utils/countrySlug";

function asList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

export default function useCountryLeagues(countrySlug) {
  const [country, setCountry] = useState(null);
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(!!countrySlug);
  const [error, setError] = useState(null);
  const [resolvedSlug, setResolvedSlug] = useState(null);

  useEffect(() => {
    const slug = String(countrySlug || "").trim();
    if (!slug) {
      setCountry(null);
      setLeagues([]);
      setLoading(false);
      setError(null);
      setResolvedSlug(null);
      return undefined;
    }

    let active = true;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const countries = asList(await LeagueService.getCountries());
        if (!active) return;
        const matched = matchCountryBySlug(countries, slug);
        if (!matched?.countryId) {
          setCountry(null);
          setLeagues([]);
          setError(new Error("Country not found"));
          setResolvedSlug(slug);
          setLoading(false);
          return;
        }

        const list = asList(
          await LeagueService.getLeaguesByCountry({
            countryId: matched.countryId,
            limit: 500,
            fields:
              "id,name,nameVN,logo,isHot,slug,shortName,country,countryId,countryLogo,currentRound",
          }),
        );
        if (!active) return;
        setCountry(matched);
        setLeagues(list);
        setResolvedSlug(slug);
        setLoading(false);
      } catch (err) {
        if (!active) return;
        console.error(err);
        setCountry(null);
        setLeagues([]);
        setError(err);
        setResolvedSlug(slug);
        setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [countrySlug]);

  const leagueIds = useMemo(
    () =>
      new Set(
        leagues
          .map((l) => String(l?.id ?? ""))
          .filter(Boolean),
      ),
    [leagues],
  );

  const waiting = !!countrySlug && (loading || resolvedSlug !== countrySlug);

  return {
    country: resolvedSlug === countrySlug ? country : null,
    leagues: resolvedSlug === countrySlug ? leagues : [],
    leagueIds: resolvedSlug === countrySlug ? leagueIds : new Set(),
    loading: waiting,
    error: resolvedSlug === countrySlug ? error : null,
  };
}
